import { useState, useEffect } from 'react';
import AppName from './AppName';
import './PWAInstallPrompt.css';

export default function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [show, setShow] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Already installed as PWA
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone) {
      setIsInstalled(true);
      return;
    }

    // Dismissed recently (don't show again for 3 days)
    const dismissed = localStorage.getItem('pwa-prompt-dismissed');
    if (dismissed && Date.now() - parseInt(dismissed) < 3 * 24 * 60 * 60 * 1000) {
      return;
    }

    // Detect iOS Safari
    const ios = /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;
    const safari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    if (ios && safari) {
      setIsIOS(true);
      // Show iOS prompt after 3 seconds
      const t = setTimeout(() => setShow(true), 3000);
      return () => clearTimeout(t);
    }

    // Android / Chrome — wait for beforeinstallprompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShow(true);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // Listen for successful install
    window.addEventListener('appinstalled', () => {
      setShow(false);
      setIsInstalled(true);
    });

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setShow(false);
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShow(false);
    localStorage.setItem('pwa-prompt-dismissed', Date.now().toString());
  };

  if (isInstalled || !show) return null;

  return (
    <div className="pwa-install-banner">
      <div className="pwa-install-inner">
        <div className="pwa-install-icon">
          <img src="/icons/icon-192.png" alt="App icon" />
        </div>
        <div className="pwa-install-text">
          <strong>Install <AppName /></strong>
          {isIOS ? (
            <span>
              Tap <span className="pwa-share-icon">&#x2BAD;</span> then
              <b> "Add to Home Screen"</b>
            </span>
          ) : (
            <span>Install the app for faster access</span>
          )}
        </div>
        <div className="pwa-install-actions">
          {!isIOS && (
            <button className="pwa-btn-install" onClick={handleInstall}>
              Install
            </button>
          )}
          <button className="pwa-btn-dismiss" onClick={handleDismiss} aria-label="Dismiss">
            ✕
          </button>
        </div>
      </div>
      {isIOS && (
        <div className="pwa-ios-arrow">
          <svg width="24" height="16" viewBox="0 0 24 16">
            <path d="M12 16 L0 0 L24 0 Z" fill="#1e293b" />
          </svg>
        </div>
      )}
    </div>
  );
}
